// What one outside party sees on a deal. Shared by the agent's "Preview" dialog and the
// party's own page (/room/:token) so the two can never drift apart.

export function fmtDate(d) {
  if (!d) return "Not set";
  return new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" });
}

export function fmtDeadline(local) {
  if (!local) return "No deadline";
  const [d, t] = local.split("T");
  const day = new Date(`${d}T00:00:00`).toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
  const [h, m] = (t || "23:59").split(":").map(Number);
  return `${day}, ${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"} ET`;
}

/** "Jan 15, 2027" in Toronto time from an ISO instant. */
export function fmtInstant(iso, withTime = false) {
  if (!iso) return "";
  const opts = { timeZone: "America/Toronto", year: "numeric", month: "short", day: "numeric" };
  if (withTime) Object.assign(opts, { hour: "numeric", minute: "2-digit" });
  return new Date(iso).toLocaleString("en-US", opts);
}

export function addressLine(property) {
  if (!property) return "";
  return property.unit ? `Unit ${property.unit}, ${property.address}` : property.address;
}

export function RoomBlock({ title, children, empty, p, testid, note }) {
  const items = (Array.isArray(children) ? children.flat() : [children]).filter(Boolean);
  return (
    <section data-testid={testid} style={{ minWidth: 0 }}>
      <h2 style={{ fontSize: 15, fontWeight: 500, color: p.ink, margin: "0 0 6px", letterSpacing: -0.1 }}>{title}</h2>
      {note ? <div style={{ fontSize: 12.5, color: p.ink3, margin: "-2px 0 8px" }}>{note}</div> : null}
      <div style={{ borderTop: `1px solid ${p.hair}` }}>
        {items.length ? items : <div style={{ fontSize: 13, color: p.ink3, padding: "12px 0" }}>{empty}</div>}
      </div>
    </section>
  );
}

export function RoomLine({ left, sub, right, action, p, testid }) {
  return (
    <div data-testid={testid} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: `1px solid ${p.hair}`, minWidth: 0, minHeight: 44 }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 14, color: p.ink, overflowWrap: "anywhere" }}>{left}</div>
        {sub ? <div style={{ fontSize: 12.5, color: p.ink3, marginTop: 2, overflowWrap: "anywhere" }}>{sub}</div> : null}
      </div>
      {right || action ? (
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
          {right ? <div style={{ fontSize: 13, color: p.ink2, whiteSpace: "nowrap" }}>{right}</div> : null}
          {action}
        </div>
      ) : null}
    </div>
  );
}

/** Quiet underlined text action, 44px tall hit area. */
export function RoomAction({ children, onClick, p, testid, disabled, label }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} data-testid={testid} aria-label={label}
      style={{ background: "none", border: "none", padding: "0 2px", minHeight: 44, cursor: disabled ? "default" : "pointer", color: disabled ? p.ink3 : p.ink2,
        fontFamily: "inherit", fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: p.hairS || p.hair, whiteSpace: "nowrap" }}>
      {children}
    </button>
  );
}

export function RoomDates({ v, p }) {
  return (
    <RoomBlock title="Key dates" p={p} empty="No dates shared with you." testid="room-dates">
      {v.dates.map((d) => <RoomLine key={d.key} p={p} left={d.label} right={fmtDate(d.date)} />)}
      {v.price && v.price.sale_price ? <RoomLine p={p} left="Sale price" right={v.price.sale_price} /> : null}
      {v.price && v.price.deposit ? <RoomLine p={p} left="Deposit" right={v.price.deposit} /> : null}
    </RoomBlock>
  );
}

export function RoomConditions({ v, p, condAction, after, saidText = "They said it's met" }) {
  return (
    <RoomBlock title="Conditions" p={p} empty="No conditions shared with you." testid="room-conditions">
      {v.conditions.map((c) => (
        <div key={c.id} style={{ minWidth: 0 }}>
          <RoomLine p={p} testid={`room-condition-${c.id}`} left={c.label}
            sub={c.said_met_at ? `${fmtDeadline(c.deadline_local)} · ${saidText}` : fmtDeadline(c.deadline_local)} right={c.state_label}
            action={condAction ? condAction(c) : null} />
          {after ? after(c) : null}
        </div>
      ))}
    </RoomBlock>
  );
}

export function RoomSent({ v, p, title = "What they've sent", empty }) {
  if (!v.sent || (!v.sent.length && !empty)) return null;
  return (
    <RoomBlock title={title} p={p} empty={empty} testid="room-sent">
      {v.sent.map((d) => (
        <RoomLine key={d.id} p={p} testid={`room-sent-${d.id}`} left={d.name} sub={d.at ? `Sent ${fmtInstant(d.at, true)}` : null} right={d.state_label} />
      ))}
    </RoomBlock>
  );
}

export function RoomDocuments({ v, p, docAction, note }) {
  return (
    <RoomBlock title="Documents" p={p} empty="No documents shared with you." testid="room-documents" note={note}>
      {v.documents.map((d) => (
        <RoomLine key={d.id} p={p} testid={`room-document-${d.id}`} left={d.name} sub={d.doc_type} action={docAction ? docAction(d) : null} />
      ))}
    </RoomBlock>
  );
}

export function RoomAgent({ v, p }) {
  if (!v.agent || !v.agent.name) return null;
  const a = v.agent;
  const link = { color: p.ink2, textDecoration: "underline", textUnderlineOffset: 3, textDecorationColor: p.hairS || p.hair, overflowWrap: "anywhere",
    display: "inline-flex", alignItems: "center", minHeight: 44, alignSelf: "flex-start", margin: "-6px 0" };
  return (
    <RoomBlock title="Shared by" p={p} testid="room-agent">
      <div style={{ padding: "12px 0", borderBottom: `1px solid ${p.hair}`, display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <div style={{ fontSize: 14, color: p.ink }}>{a.name}</div>
        {a.brokerage ? <div style={{ fontSize: 13, color: p.ink3 }}>{a.brokerage}</div> : null}
        {a.email ? <a href={`mailto:${a.email}`} style={{ ...link, fontSize: 13 }}>{a.email}</a> : null}
        {a.phone ? <a href={`tel:${a.phone.replace(/[^\d+]/g, "")}`} style={{ ...link, fontSize: 13 }}>{a.phone}</a> : null}
      </div>
    </RoomBlock>
  );
}

/** Compact, stacked view used inside the agent's preview dialog. */
export default function RoomView({ v, p }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, paddingTop: 16, minWidth: 0 }}>
      <div style={{ border: `1px solid ${p.hair}`, borderRadius: 2, padding: 16 }}>
        <div style={{ fontSize: 12.5, color: p.ink3 }}>{v.party.role_label}{v.party.company ? ` · ${v.party.company}` : ""}</div>
        <div style={{ fontSize: 17, fontWeight: 500, color: p.ink, marginTop: 4, overflowWrap: "anywhere" }}>{addressLine(v.property)}</div>
        <div style={{ fontSize: 13, color: p.ink3, marginTop: 2 }}>{[v.property.city, v.property.province].filter(Boolean).join(", ")}</div>
      </div>
      <RoomDates v={v} p={p} />
      <RoomConditions v={v} p={p} />
      <RoomDocuments v={v} p={p} />
      <RoomSent v={v} p={p} />
      {v.hand_back ? <div style={{ fontSize: 12.5, color: p.ink3 }}>They can send you documents and tell you when a condition is met. Nothing on the deal changes until you do it.</div> : null}
      <RoomAgent v={v} p={p} />
    </div>
  );
}
